/**
 * Sprint 23.8 → 26.0 — WorkspaceActiveTasks (hero section 5: "status").
 *
 * The cross-agent open-tasks roll-up — the workspace answer to "what is everyone
 * working on?" that the per-agent list can't give. Up to 5 flat rows, plus a
 * "See all N →" that switches to the Tasks tab. A row click opens the task
 * detail page (/tasks/:id).
 *
 * Sprint 26.0 — repointed from tasks to the one task noun. Rows are flat
 * (no runtime status pills per the design rules): title + owning agent.
 */

import { type CSSProperties } from "react";
import { useNavigate } from "react-router";
import { getAgentPersona } from "../cockpit/CockpitHeader";
import { Avatar } from "../primitives";
import { useWorkspaceTasks } from "../../hooks/use-workspace-tasks";

export function WorkspaceActiveTasks() {
  const navigate = useNavigate();
  const query = useWorkspaceTasks({ limit: 5 });
  const tasks = query.data?.tasks ?? [];
  const activeCount = query.data?.active_count ?? 0;

  return (
    <section>
      <div style={headerRow}>
        <h3 style={title}>Open tasks</h3>
        {activeCount > 5 && (
          <button type="button" style={seeAll} onClick={() => navigate("/home#tasks")}>
            See all {activeCount} →
          </button>
        )}
      </div>

      {tasks.length === 0 ? (
        <div style={empty}>No open tasks — assign one to get started.</div>
      ) : (
        <div style={list}>
          {tasks.map((t) => (
            <div
              key={t.id}
              style={row}
              role="button"
              tabIndex={0}
              onClick={() => navigate(`/tasks/${t.id}`)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  navigate(`/tasks/${t.id}`);
                }
              }}
            >
              <Avatar
                persona={getAgentPersona({ id: t.agent_id, path: t.agent_path })}
                name={t.agent_name}
                size="sm"
              />
              <span style={rowTitle}>{t.title}</span>
              <span style={rowMeta}>{t.agent_name}</span>
              <i className="ti ti-chevron-right" style={chevron} aria-hidden="true" />
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const headerRow: CSSProperties = {
  display: "flex",
  alignItems: "baseline",
  justifyContent: "space-between",
  marginBottom: 8,
};

const title: CSSProperties = {
  margin: 0,
  fontSize: 12,
  fontWeight: 500,
  color: "var(--color-text-secondary)",
  textTransform: "uppercase",
  letterSpacing: "0.5px",
};

const seeAll: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-info)",
  cursor: "pointer",
  background: "transparent",
  border: "none",
  fontFamily: "var(--font-sans)",
  padding: 0,
};

const list: CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 6,
};

const row: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  padding: "8px 10px",
  borderRadius: "var(--border-radius-md)",
  border: "0.5px solid var(--color-border-tertiary)",
  background: "var(--color-background-primary)",
  cursor: "pointer",
  minWidth: 0,
};

const rowTitle: CSSProperties = {
  fontSize: 13,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: 1,
  minWidth: 0,
};

const rowMeta: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const chevron: CSSProperties = {
  fontSize: 14,
  color: "var(--color-text-tertiary)",
  flexShrink: 0,
};

const empty: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  padding: "8px 0",
};
